// DiaporamaBox.jsx
import React, { useRef, useEffect, useState, useMemo } from 'react';
import { FaExpand, FaMobileAlt } from 'react-icons/fa';
import { useEventDetailContext } from '../contexts/EventDetailContext';
import { useBackgroundContext } from '../contexts/BackgroundContext';
import { useDiaporamaSettings } from '../contexts/DiaporamaSettingsContext';
import { useDiaporamaGlobal } from '../contexts/DiaporamaGlobalContext';
import { QRCodeSVG } from 'qrcode.react';
import './style/DiaporamaBox.css';
import { useUserDBContext } from '../contexts/UserDBContext';
import logoAppdiapo from '../assets/img/logobeonwallDiapo.png';
import Lottie from 'lottie-react';
import stopAnimation from '../assets/animations/Stop.json';
import { useTranslation } from 'react-i18next';
import startAnimationEN from '../assets/animations/Start_en.json';
import startAnimationFR from '../assets/animations/Start_fr.json';
import InstallPromptModal from '../components/InstallPromptModal';

// 🧠 Cache global basé sur item.id
const imageCache = new Map();

function DiaporamaBox({ eventId, qrCodeUrl, eventName }) {
  const boxRef = useRef(null);
  const { details } = useEventDetailContext();
  const {
    isRunning,
    setIsRunning,
    index,
    setIndex,
    displayCount,
    setDisplayCount
  } = useDiaporamaGlobal();

  const { backgrounds } = useBackgroundContext();

  const {
    fontColor,
    bgColor,
    fontFamily,
    fontSize,
    backgroundImage,
    showScanMe,
    scanInterval,
    transitionSpeed,
    showQrCorner,
    bannerEnabled,
    bannerPosition,
    bannerText,
    bannerShowBackground,
    bannerBgColor,
    bannerTextColor,
    bannerFontSize,
    qrCornerPosition,
    qrCornerSize,
    animationType,
    animationEnabled,
    watermarkEnabled
  } = useDiaporamaSettings();

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 600);
  const itemsRef = useRef([]);

  const { userDBData } = useUserDBContext();
  const subscription = userDBData?.subscriptions; // fallback P1
  const isWatermarkMandatory = ['P0', 'P1'].includes(subscription);
  const showWatermark = isWatermarkMandatory || Boolean(watermarkEnabled);

  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;
  const startAnimation = currentLang === 'fr' ? startAnimationFR : startAnimationEN;
  const isMobile = useMemo(() => window.innerWidth <= 768, []);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [isIphone, setIsIphone] = useState(false); // (laissé tel quel)

  // 🆕 aide (ajout minimal)
  const helperText = isRunning
    ? t('diaporamabox.liveNow')
    : t('diaporamabox.instructions');

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 500);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const items = useMemo(() => {
    const filtered = [...details]
      .filter(item => String(item.eventId) === String(eventId))
      .filter(item => item.signedUrl || item.commentaire)
      .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    itemsRef.current = filtered;
    return filtered;
  }, [details, eventId]);

  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(() => {
      const currentItems = itemsRef.current;
      if (currentItems.length === 0) return;
      setDisplayCount(prev => prev + 1);
      setIndex(prevIndex => (prevIndex + 1) % currentItems.length);
    }, transitionSpeed * 1000);
    return () => clearInterval(interval);
  }, [isRunning, transitionSpeed]);

  const isInFullscreen = () =>
    document.fullscreenElement === boxRef.current ||
    document.webkitFullscreenElement === boxRef.current ||
    document.mozFullScreenElement === boxRef.current ||
    document.msFullscreenElement === boxRef.current;

  const toggleFullscreen = () => {
    const elem = boxRef.current;
    if (!isInFullscreen() && elem) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => setIsFullscreen(true));
      } else {
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => setIsFullscreen(false));
      } else {
        setIsFullscreen(false);
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(isInFullscreen());
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const showQrCode = showScanMe && displayCount > 0 && displayCount % scanInterval === 0;
  const current = items[index];
  const isText = !showQrCode && current && !current.signedUrl;

  // ✅ Préchargement de l'image suivante
  useEffect(() => {
    if (!items || items.length === 0) return;

    const nextIndex = (index + 1) % items.length;
    const nextItem = items[nextIndex];

    if (nextItem?.signedUrl && !imageCache.has(nextItem.id)) {
      imageCache.set(nextItem.id, nextItem.signedUrl);
      const img = new Image();
      img.src = nextItem.signedUrl;
      console.log(`[PRELOAD] Image ${nextItem.id} ajoutée au cache et préchargée`);
    } else {
      console.log(`[PRELOAD] Image ${nextItem?.id} déjà dans le cache`);
    }
  }, [index, items]);

  const renderContent = () => {
    const animationClass = animationEnabled ? animationType : '';

    if (items.length === 0) {
      return (
        <div className={`diaporama-box-content ${animationClass}`} key={`empty-${displayCount}`}>
          <div className="diaporama-box-content-inner">
            <div style={{ textAlign: 'center' }}>
              <h4 style={{ marginBottom: '10px', fontSize: '36px' }}>{t('diaporamabox.Scanme')}</h4>
              <QRCodeSVG
                value={qrCodeUrl}
                size={
                  isFullscreen
                    ? isMobile
                      ? 300
                      : 500
                    : 200
                }
              />
              <p style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '10px' }}>{t('DiaporamaFirstMessage.text')}</p>
            </div>
          </div>
        </div>
      );
    }

    if (showQrCode) {
      return (
        <div className={`diaporama-box-content ${animationClass}`} key={`qr-${displayCount}`}>
          <div className="diaporama-box-content-inner">
            <div style={{ textAlign: 'center' }}>
              <h4 style={{ marginBottom: '10px', fontSize: '36px' }}>{t('diaporamabox.Scanme')}</h4>
              <QRCodeSVG
                value={qrCodeUrl}
                size={
                  isFullscreen
                    ? isMobile
                      ? 300
                      : 500
                    : 200
                }
              />
              <p style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '10px' }}>{eventName}</p>
            </div>
          </div>
        </div>
      );
    }

    const currentItem = items[index];

    if (currentItem?.signedUrl) {
      if (!imageCache.has(currentItem.id)) {
        imageCache.set(currentItem.id, currentItem.signedUrl);
        console.log(`[CACHE] Init image ${currentItem.id}`);
      } else {
        console.log(`[CACHE] Déjà en cache ${currentItem.id}, on ignore`);
      }

      const cachedUrl = imageCache.get(currentItem.id);

      return (
        <div className={`diaporama-box-content ${animationClass}`} key={`img-${displayCount}`}>
          <div className="diaporama-box-content-inner">
            <div className="image-wrapper">
              {showWatermark && <img src={logoAppdiapo} className="diapo-logo inline-left" alt="logo left" />}
              <img
                className={`diaporama-image ${isFullscreen ? 'fullscreen-image' : ''}`}
                src={cachedUrl}
                alt="diapo"
                style={{
                  maxWidth: isFullscreen && showWatermark ? '80vw' : undefined
                }}
              />
              {showWatermark && <img src={logoAppdiapo} className="diapo-logo inline-right" alt="logo right" />}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`diaporama-box-content ${animationClass}`} key={`txt-${displayCount}`}>
        <div className="diaporama-box-content-inner">
          <div className="image-wrapper" style={{ width: '100%' }}>
            {showWatermark && <img src={logoAppdiapo} className="diapo-logo inline-left" alt="logo left" />}
            <div
              className="diaporama-text"
              style={{
                fontSize,
                fontFamily,
                color: fontColor,
                textShadow: backgroundImage ? '1px 1px 2px rgba(0,0,0,0.6)' : 'none'
              }}
            >
              <div className="diaporama-text-inner">
                {currentItem?.commentaire || <em>{t('diaporamabox.emptycomments')}</em>}
              </div>
            </div>
            {showWatermark && <img src={logoAppdiapo} className="diapo-logo inline-right" alt="logo right" />}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="diaporama-container">
      <div
        className="diaporama-button-container"
        style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
      >
        <div
          className="diaporama-button-wrapper"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '20px',
            position: 'relative',
          }}
        >
          {/* Bouton iPhone à droite — stylé via CSS */}
          {isSmallScreen && (
            <>
              <button
                onClick={() => setShowInstallModal(true)}
                className="install-button"
                title="Installer sur iPhone"
              >
                <FaMobileAlt style={{ fontSize: '18px' }} />
              </button>
              {showInstallModal && (
                <InstallPromptModal onClose={() => setShowInstallModal(false)} />
              )}
            </>
          )}

          {/* 🆕 Texte d’aide discret */}
          <div
            aria-live="polite"
            className="diapo-helper"
          >
            {helperText}
          </div>

          {/* Bouton Démarrer centré */}
          <button
            onClick={() => {
              if (!isRunning) {
                setDisplayCount(0);
                setIndex(0);
              }
              setIsRunning(!isRunning);
            }}
            className={`diaporama-toggle-button ${isRunning ? 'running' : ''}`}
            style={{
              width: 200,
              height: 200,
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <Lottie
              animationData={isRunning ? stopAnimation : startAnimation}
              loop
              autoplay
              style={{
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                marginTop: '-180px',
              }}
            />
          </button>
        </div>
      </div>

      <div
        ref={boxRef}
        className={`diaporama-box ${isFullscreen ? 'fullscreen-fallback' : ''} ${isFullscreen && isMobile ? 'fullscreen-mobile-fix' : ''}`}
        style={{
          marginTop: isMobile && isFullscreen ? '0' : '-160px',
          backgroundColor:
            isText && !backgroundImage
              ? bgColor
              : current?.signedUrl && isFullscreen
              ? 'black'
              : undefined,
          backgroundImage:
            isText && backgroundImage
              ? `url(${backgroundImage})`
              : undefined,
          color: fontColor,
        }}
      >
        <button
          onClick={toggleFullscreen}
          className="diaporama-fullscreen-btn"
          style={{ color: fontColor }}
          title="Plein écran"
        >
          <FaExpand />
        </button>

        {renderContent()}

        {bannerEnabled && (
          <div style={{
            position: 'absolute',
            top: bannerPosition === 'hautcentre' ? '20px' : 'unset',
            bottom: bannerPosition === 'bascentre' ? '20px' : 'unset',
            left: '50%',
            transform: 'translateX(-50%)',
            background: bannerShowBackground ? bannerBgColor : 'transparent',
            color: bannerTextColor,
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: bannerFontSize,
            maxWidth: '80%',
            textAlign: 'center'
          }}>
            {bannerText}
          </div>
        )}

        {showQrCorner && (
          <div className={`qr-corner ${qrCornerPosition}`}>
            <div
              className="qr-label"
              style={{
                fontSize: `${Math.max(10, Math.round(qrCornerSize * 0.12))}px`,
                marginBottom: `${Math.max(4, Math.round(qrCornerSize * 0.12))}px`,
              }}
            >
              {t('diaporamabox.Scanme') || 'Scan me'}
            </div>

            <QRCodeSVG value={qrCodeUrl} size={qrCornerSize} />
          </div>
        )}

      </div>
    </div>
  );
}

export default DiaporamaBox;
